import { investmentTableTotalRange, type AmountRange } from '../../../../backend/src/utils/quoteTotal';
import { investmentRowShowsHours, investmentTableShowsHours } from '../../../../backend/src/utils/investmentRowDisplay';
import InvestmentProductPublicRow from './InvestmentProductPublicRow';
import { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import type { CSSProperties } from 'react';
import type { Block, InvestmentBlockContent, InvestmentRateCard, InvestmentTable } from '@/types';
import { uboTheme, themeColors } from '@/theme/uboTheme';
import PageHeader from './PageHeader';
import {
  useQuoteClientLogo,
  useQuotePageBrand,
  useResolvedAgencyLogoUrl,
} from '@/store/quoteClientLogoContext';
import { formatCurrency, formatCurrencyRange } from '@/lib/format';
import {
  getInvestmentTableLabels,
  normalizeInvestmentLabelLanguage,
} from '@/editor/investmentTableLabels';
import { sanitizeInvestmentDescriptionHtml } from '@/editor/investmentTableCellContent';
import { normalizedInvestmentColumnWidths } from '@/editor/investmentColumnWidths';
import { useQuoteDocumentMode } from '@/components/quote-document/QuoteDocument';
import { A4_LANDSCAPE } from '@/constants/pageFormat';

interface InvestmentBlockProps {
  block: Block;
  /** Publieke/PDF: laatste subpagina geen dubbele break vóór volgend blok. */
  isPublic?: boolean;
  /** Publieke PDF: laat Puppeteer wachten tot tabel-paginering klaar is. */
  onMeasureComplete?: () => void;
}

type RenderedInvestmentTable = InvestmentTable & {
  chunkKey?: string;
  renderTotal?: AmountRange;
};

const PAGE_HEIGHT_MM = 210;
const PAGE_PADDING_PX = 50;
const HEADER_HEIGHT_PX = 50;
const HEADER_GAP_PX = 24;
const TITLE_HEIGHT_PX = 60;
const MM_TO_PX = 3.7795275591;
const AVAILABLE_FIRST_PAGE =
  PAGE_HEIGHT_MM * MM_TO_PX -
  PAGE_PADDING_PX * 2 -
  HEADER_HEIGHT_PX -
  HEADER_GAP_PX -
  TITLE_HEIGHT_PX;
const AVAILABLE_CONTINUATION =
  PAGE_HEIGHT_MM * MM_TO_PX - PAGE_PADDING_PX * 2 - HEADER_HEIGHT_PX - HEADER_GAP_PX;

function normalizedInvestmentPricingType(table: InvestmentTable): 'hourly' | 'fixed' | 'recurring' {
  const pt = table.pricingType;
  return pt === 'hourly' || pt === 'fixed' ? pt : 'recurring';
}

const calcTotal = (table: InvestmentTable, onlySelected = false): AmountRange => {
  const renderTotal = (table as RenderedInvestmentTable).renderTotal;
  if (renderTotal) return renderTotal;

  return investmentTableTotalRange({ ...table, pricingType: normalizedInvestmentPricingType(table) }, { onlySelected });
};

function PlainInvestmentDescription({ title }: { title: string }) {
  const paragraphs = title.split(/\r?\n/);
  return (
    <div className="investment-description-rich">
      {paragraphs.map((paragraph, index) => (
        <p key={`${index}-${paragraph}`} className="whitespace-pre-wrap">
          {paragraph}
        </p>
      ))}
    </div>
  );
}

function InvestmentTableView({
  table,
  labels,
  colors,
  isUbx,
  currency = 'EUR',
  showHourColumns = true,
}: {
  table: RenderedInvestmentTable;
  labels: {
    description: string;
    hourlyRate: string;
    priceExTax: string;
    amountHours: string;
    subtotal: string;
    exTax: string;
    totalRow: string;
  };
  colors: ReturnType<typeof themeColors>;
  isUbx: boolean;
  currency?: string;
  /** Legacy blokwaarde; de tabel zelf wint. Zie InvestmentTable.showHourColumns. */
  showHourColumns?: boolean;
}) {
  const pt = normalizedInvestmentPricingType(table);
  const hasGroups = table.rows.some((row) => row.investmentProduct || row.productPrice);
  /** Uurtabel zonder uren: alleen omschrijving + bedrag, zoals bij terugkerend. */
  const defaultShowHours = (table.showHourColumns ?? showHourColumns) !== false;
  /** Uit: alleen omschrijvingen, het bedrag staat enkel in de totaalregel. */
  const pricesVisible = table.showRowPrices !== false;
  const hoursVisible = pricesVisible && investmentTableShowsHours(table.rows, pt, defaultShowHours);
  const columnWidths = normalizedInvestmentColumnWidths(pt, table.columnWidths);
  const tableRule = colors.investmentTableRule ?? colors.primary;
  const rowBorder = isUbx ? 'rgba(0, 158, 145, 0.18)' : '#e4e8ef';
  const headerCellStyle: CSSProperties = {
    backgroundColor: colors.tableHeaderBg,
    borderBottom: '1px solid #d9dfea',
    height: 'auto',
    minHeight: '42px',
    padding: '10px 12px',
    fontWeight: 600,
    fontSize: '12px',
    lineHeight: '16px',
    color: '#3c4658',
  };
  const bodyCellStyle: CSSProperties = {
    height: 'auto',
    minHeight: '52px',
    padding: '12px',
    borderBottom: `1px solid ${rowBorder}`,
    color: '#10172a',
    verticalAlign: 'middle',
    fontSize: '13px',
  };

  return (
    <div className="mb-8" data-investment-table={table.id}>
      <h3
        data-investment-table-title
        className="mb-2 mt-8"
        style={{
          fontSize: '20px',
          fontWeight: 900,
          textTransform: 'uppercase',
          fontFamily: uboTheme.fonts.primaryFallback,
          color: colors.primary,
        }}
      >
        {table.title}
      </h3>
      {table.subtitle ? (
        <p
          className="-mt-2 mb-4 text-[13px] leading-snug"
          style={{ color: colors.secondary, opacity: 0.75 }}
        >
          {table.subtitle}
        </p>
      ) : null}

      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
          borderTop: `2px solid ${tableRule}`,
          borderBottom: '1px solid #d9dfea',
          backgroundColor: '#ffffff',
          fontFamily: "'Open Sans', 'Helvetica Neue', Helvetica, Arial, sans-serif",
          fontSize: '13px',
          lineHeight: 1.45,
        }}
      >
        <colgroup>
          <col
            style={
              pricesVisible && columnWidths.description
                ? { width: columnWidths.description }
                : undefined
            }
          />
          {hoursVisible ? (
            <>
              <col style={{ width: columnWidths.hourlyRate }} />
              <col style={{ width: columnWidths.hours }} />
              <col style={{ width: columnWidths.subtotal }} />
            </>
          ) : null}
          {pricesVisible && pt === 'hourly' && !hoursVisible ? (
            <col style={{ width: columnWidths.subtotal }} />
          ) : null}
          {pricesVisible && pt === 'recurring' ? <col style={{ width: columnWidths.subtotal }} /> : null}
        </colgroup>
        <thead data-investment-table-head>
          <tr>
            <th style={{ ...headerCellStyle, textAlign: 'left' }}>{labels.description}</th>
            {hoursVisible && (
              <>
                <th style={{ ...headerCellStyle, textAlign: 'right' }}>
                  {labels.hourlyRate ?? labels.priceExTax}
                </th>
                <th style={{ ...headerCellStyle, textAlign: 'right' }}>{labels.amountHours}</th>
              </>
            )}
            {pricesVisible && pt === 'hourly' && (
              <th style={{ ...headerCellStyle, textAlign: 'right' }}>{labels.subtotal}</th>
            )}
            {pricesVisible && pt === 'recurring' && (
              <th style={{ ...headerCellStyle, textAlign: 'right' }}>{labels.priceExTax}</th>
            )}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row) => {
            if (row.investmentProduct || row.productPrice) return <InvestmentProductPublicRow
              key={row.id} row={row} currency={currency} hoursVisible={hoursVisible}
              pricesVisible={pricesVisible} cellStyle={bodyCellStyle} />;
            const rowBg = 'transparent';
            const rowHours = investmentRowShowsHours(row, defaultShowHours);
            return (
              <tr
                key={row.id}
                data-investment-row={row.id}
                style={{ opacity: row.optional && !row.selected ? 0.5 : 1 }}
              >
                <td style={{ ...bodyCellStyle, backgroundColor: rowBg, textAlign: 'left' }}>
                  <div>
                    {row.titleHtml ? (
                      <div
                        className="investment-description-rich leading-snug [&_strong]:font-semibold [&_b]:font-semibold [&_ul]:my-0 [&_ol]:my-0"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeInvestmentDescriptionHtml(row.titleHtml),
                        }}
                      />
                    ) : (
                      <PlainInvestmentDescription title={row.title} />
                    )}
                  </div>
                </td>
                {!row.useText && hoursVisible && (
                  <>
                    <td
                      style={{
                        ...bodyCellStyle,
                        backgroundColor: rowBg,
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {rowHours ? formatCurrency(row.price || 0, currency) : ''}
                    </td>
                    <td
                      style={{
                        ...bodyCellStyle,
                        backgroundColor: rowBg,
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {rowHours ? row.hours : ''}
                    </td>
                  </>
                )}
                {pricesVisible && !row.useText && pt === 'hourly' && (
                  <td
                    style={{
                      ...bodyCellStyle,
                      backgroundColor: rowBg,
                      textAlign: 'right',
                      fontVariantNumeric: row.subtotalText ? 'normal' : 'tabular-nums',
                    }}
                  >
                    {row.subtotalText ||
                      formatCurrency((row.price || 0) * (row.hours || 0), currency)}
                  </td>
                )}
                {row.useText && hoursVisible && (
                  <>
                    <td style={{ ...bodyCellStyle, backgroundColor: rowBg, textAlign: 'right' }}>
                      {rowHours ? row.text : ''}
                    </td>
                    <td
                      style={{
                        ...bodyCellStyle,
                        backgroundColor: rowBg,
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {rowHours ? row.hours || '' : ''}
                    </td>
                  </>
                )}
                {pricesVisible && row.useText && pt === 'hourly' && (
                  <td style={{ ...bodyCellStyle, backgroundColor: rowBg, textAlign: 'right' }}>
                    {row.subtotalText || row.text}
                  </td>
                )}
                {pricesVisible && !row.useText && pt === 'recurring' && (
                  <td
                    style={{
                      ...bodyCellStyle,
                      backgroundColor: rowBg,
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatCurrency(row.subtotal || 0, currency)}
                  </td>
                )}
                {pricesVisible && row.useText && pt === 'recurring' && (
                  <td style={{ ...bodyCellStyle, backgroundColor: rowBg, textAlign: 'right' }}>
                    {row.text}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>

      {table.showTotalRow !== false && (
        <div
          data-investment-total-row
          style={{
            display: 'flex',
            minHeight: '54px',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '24px',
            borderTop: `2px solid ${tableRule}`,
            backgroundColor: colors.tableHeaderBg,
            padding: '12px 14px',
          }}
        >
          <span className="text-[14px] font-semibold" style={{ color: '#10172a' }}>
            {labels.totalRow ?? labels.exTax}
          </span>
          <span
            className="text-right text-[14px] font-bold"
            style={{ color: '#10172a', fontVariantNumeric: 'tabular-nums' }}
          >
            {pt === 'fixed' && !hasGroups && table.fixedTotalText
              ? table.fixedTotalText
              : formatCurrencyRange(calcTotal(table, true).min, calcTotal(table, true).max, currency)}
          </span>
        </div>
      )}
    </div>
  );
}

function InvestmentRateCardView({
  rateCard,
  colors,
}: {
  rateCard: InvestmentRateCard;
  colors: ReturnType<typeof themeColors>;
}) {
  const rows = rateCard.rows ?? [];
  if (rows.length === 0) return null;
  return (
    <div
      className="mt-10 pt-6 border-t"
      style={{ borderColor: colors.investmentTableRule ?? colors.primary }}
      data-investment-rate-card
    >
      {rateCard.title ? (
        <h4
          className="mb-4 text-[15px] font-bold uppercase tracking-wide"
          style={{ fontFamily: uboTheme.fonts.primaryFallback, color: colors.primary }}
        >
          {rateCard.title}
        </h4>
      ) : null}
      <div className="space-y-2.5">
        {rows.map((r) => (
          <div key={r.id} className="flex justify-between gap-6 text-[13px]">
            <span style={{ color: colors.secondary }}>{r.label}</span>
            <span className="font-semibold shrink-0" style={{ color: colors.primary }}>
              {r.rateText}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function pageSignature(pages: RenderedInvestmentTable[][]) {
  return pages
    .map((page) =>
      page
        .map(
          (table) =>
            `${table.id}:${table.chunkKey ?? 'full'}:${table.rows.map((row) => row.id).join(',')}:${table.showTotalRow !== false}:${table.showHourColumns !== false}:${table.showRowPrices !== false}`
        )
        .join('|')
    )
    .join('||');
}

function InvestmentBlock({ block, isPublic = false, onMeasureComplete }: InvestmentBlockProps) {
  const documentMode = useQuoteDocumentMode();
  const content = block.content as InvestmentBlockContent;
  const quoteClientLogo = useQuoteClientLogo();
  const quoteBrand = useQuotePageBrand();
  const effectiveTheme = content.theme ?? quoteBrand;
  const agencyUrl = useResolvedAgencyLogoUrl(content.agencyLogoUrl, content.theme);
  const colors = themeColors(effectiveTheme);
  const tables = useMemo(() => content.tables || [], [content.tables]);
  const invCurrency = content.currency ?? 'EUR';
  /** Legacy blokwaarde voor tabellen van vóór de per-fase-instelling. */
  const showHourColumns = content.showHourColumns !== false;
  const preset = getInvestmentTableLabels(normalizeInvestmentLabelLanguage(content.labelLanguage));
  const tl = content.tableLabels;
  const labels = {
    description: tl?.description ?? preset.description,
    hourlyRate: tl?.hourlyRate ?? preset.hourlyRate,
    priceExTax: tl?.priceExTax ?? preset.priceExTax,
    amountHours: tl?.amountHours ?? preset.amountHours,
    subtotal: tl?.subtotal ?? preset.subtotal,
    exTax: tl?.exTax ?? preset.exTax,
    totalRow: tl?.totalRow ?? preset.totalRow,
  };

  const measureRef = useRef<HTMLDivElement>(null);
  const measureCompleteRef = useRef(false);
  const pageSignatureRef = useRef('');
  const [pages, setPages] = useState<RenderedInvestmentTable[][]>([tables]);
  /** Online klantweergave: tabellen lopen door i.p.v. op A4-hoogte te splitsen. */
  const paginate = !(isPublic && documentMode === 'web');

  const reportMeasureComplete = useCallback(() => {
    if (measureCompleteRef.current) return;
    measureCompleteRef.current = true;
    onMeasureComplete?.();
  }, [onMeasureComplete]);

  const measure = useCallback(() => {
    if (!paginate || !measureRef.current || tables.length === 0) {
      reportMeasureComplete();
      return;
    }

    const tableEls = measureRef.current.querySelectorAll<HTMLElement>('[data-investment-table]');
    if (tableEls.length === 0) return;

    const result: RenderedInvestmentTable[][] = [[]];
    let currentHeight = 0;
    let pageIndex = 0;

    const pushPage = () => {
      pageIndex++;
      result.push([]);
      currentHeight = 0;
    };

    const pushTable = (table: RenderedInvestmentTable, height: number) => {
      result[pageIndex].push(table);
      currentHeight += height;
    };

    tableEls.forEach((el, i) => {
      const height = el.getBoundingClientRect().height;
      const maxHeight = pageIndex === 0 ? AVAILABLE_FIRST_PAGE : AVAILABLE_CONTINUATION;

      if (currentHeight + height > maxHeight && currentHeight > 0) {
        pushPage();
      }

      const table = tables[i];
      const rowEls = Array.from(el.querySelectorAll<HTMLElement>('[data-investment-row]'));
      const rowHeights = rowEls.map((rowEl) => rowEl.getBoundingClientRect().height);
      const rowHeightTotal = rowHeights.reduce((sum, rowHeight) => sum + rowHeight, 0);
      const fixedTableHeight = Math.max(0, height - rowHeightTotal);

      if (
        height > (pageIndex === 0 ? AVAILABLE_FIRST_PAGE : AVAILABLE_CONTINUATION) &&
        rowEls.length > 1
      ) {
        let rowStart = 0;
        const fullTableTotal = calcTotal(table, true);

        while (rowStart < table.rows.length) {
          const pageMax = pageIndex === 0 ? AVAILABLE_FIRST_PAGE : AVAILABLE_CONTINUATION;
          const firstRowHeight = rowHeights[rowStart] ?? 0;

          if (currentHeight > 0 && currentHeight + fixedTableHeight + firstRowHeight > pageMax) {
            pushPage();
          }

          const chunkStart = rowStart;
          let chunkHeight = fixedTableHeight;

          while (rowStart < table.rows.length) {
            const nextRowHeight = rowHeights[rowStart] ?? 0;
            if (
              rowStart > chunkStart &&
              currentHeight + chunkHeight + nextRowHeight >
                (pageIndex === 0 ? AVAILABLE_FIRST_PAGE : AVAILABLE_CONTINUATION)
            ) {
              break;
            }
            chunkHeight += nextRowHeight;
            rowStart++;
          }

          const isFinalChunk = rowStart >= table.rows.length;
          const chunkRows = table.rows.slice(chunkStart, rowStart);
          pushTable(
            {
              ...table,
              rows: chunkRows,
              showTotalRow: isFinalChunk ? table.showTotalRow : false,
              renderTotal: isFinalChunk ? fullTableTotal : undefined,
              chunkKey: `${table.id}-${chunkStart}-${rowStart}`,
            },
            chunkHeight
          );

          if (!isFinalChunk) pushPage();
        }
        return;
      }

      pushTable(table, height);
    });

    const nextSignature = pageSignature(result);
    if (nextSignature !== pageSignatureRef.current) {
      pageSignatureRef.current = nextSignature;
      setPages(result);
    }
    reportMeasureComplete();
  }, [paginate, reportMeasureComplete, tables]);

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      measure();
    });
    return () => cancelAnimationFrame(frame);
  }, [measure]);

  if (!paginate || pages.length <= 1) {
    return (
      <div
        ref={measureRef}
        data-investment-page
        className={`w-full flex-1 relative flex flex-col ${isPublic ? '' : 'overflow-hidden'}`}
        style={{
          backgroundColor: '#ffffff',
          color: colors.secondary,
          padding: '50px',
          pageBreakAfter: isPublic ? 'auto' : 'always',
        }}
      >
        <PageHeader
          clientLogoUrl={content.clientLogoUrl ?? quoteClientLogo}
          agencyLogoUrl={agencyUrl}
          brand={effectiveTheme === 'ubx' ? 'ubx' : 'ubo'}
          variant="light"
        />
        <h2
          className="mb-8"
          style={{
            fontSize: '32px',
            fontWeight: 900,
            textTransform: 'uppercase',
            fontFamily: uboTheme.fonts.primaryFallback,
            color: colors.primary,
            marginTop: 24,
          }}
        >
          {content.mainTitle || 'Investeringsoverzicht'}
        </h2>
        {tables.map((table) => (
          <InvestmentTableView
            key={table.id}
            table={table}
            labels={labels}
            colors={colors}
            isUbx={effectiveTheme === 'ubx'}
            currency={invCurrency}
            showHourColumns={showHourColumns}
          />
        ))}
        {content.rateCard ? (
          <InvestmentRateCardView rateCard={content.rateCard} colors={colors} />
        ) : null}
      </div>
    );
  }

  return (
    <>
      {pages.map((pageTables, idx) => (
        <div
          key={`${block.id}-inv-page-${idx}`}
          data-investment-page
          className={`w-full relative flex flex-col ${isPublic ? '' : 'overflow-hidden'}`}
          style={{
            backgroundColor: '#ffffff',
            color: colors.secondary,
            padding: '50px',
            pageBreakAfter: isPublic && idx === pages.length - 1 ? 'auto' : 'always',
            ...(isPublic
              ? {
                  width: '100%',
                  maxWidth: '297mm',
                  minHeight: '210mm',
                  height: 'auto',
                  overflow: 'visible',
                  marginBottom:
                    documentMode === 'web' && idx < pages.length - 1
                      ? A4_LANDSCAPE.pageGap
                      : undefined,
                }
              : {
                  height: '210mm',
                  width: '297mm',
                  boxSizing: 'border-box',
                }),
          }}
        >
          <PageHeader
            clientLogoUrl={content.clientLogoUrl ?? quoteClientLogo}
            agencyLogoUrl={agencyUrl}
            brand={effectiveTheme === 'ubx' ? 'ubx' : 'ubo'}
            variant="light"
          />
          {idx === 0 && (
            <h2
              className="mb-8"
              style={{
                fontSize: '32px',
                fontWeight: 900,
                textTransform: 'uppercase',
                fontFamily: uboTheme.fonts.primaryFallback,
                color: colors.primary,
                marginTop: 24,
              }}
            >
              {content.mainTitle || 'Investeringsoverzicht'}
            </h2>
          )}
          <div style={{ marginTop: idx > 0 ? 24 : 0 }}>
            {pageTables.map((table) => (
              <InvestmentTableView
                key={table.chunkKey ?? table.id}
                table={table}
                labels={labels}
                colors={colors}
                isUbx={effectiveTheme === 'ubx'}
                currency={invCurrency}
                showHourColumns={showHourColumns}
              />
            ))}
            {idx === pages.length - 1 && content.rateCard ? (
              <InvestmentRateCardView rateCard={content.rateCard} colors={colors} />
            ) : null}
          </div>
        </div>
      ))}
    </>
  );
}

export default InvestmentBlock;
